export function quadraticBezierPoints(a, b, c, steps = 48) {
  const points = [];
  const stepCount = Math.max(6, Number(steps) || 48);
  for (let i = 0; i <= stepCount; i += 1) {
    const t = i / stepCount;
    const mt = 1 - t;
    const lat = (mt * mt * a[0]) + (2 * mt * t * b[0]) + (t * t * c[0]);
    const lon = (mt * mt * a[1]) + (2 * mt * t * b[1]) + (t * t * c[1]);
    points.push([lat, lon]);
  }
  return points;
}

export function buildArcPolyline(from, to, steps = 56) {
  const a = [Number(from?.[0]), Number(from?.[1])];
  const c = [Number(to?.[0]), Number(to?.[1])];
  if (!Number.isFinite(a[0]) || !Number.isFinite(a[1]) || !Number.isFinite(c[0]) || !Number.isFinite(c[1])) return [];

  const mid = [(a[0] + c[0]) / 2, (a[1] + c[1]) / 2];
  const dx = c[1] - a[1];
  const dy = c[0] - a[0];
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;

  const nx = (-dy) / dist;
  const ny = (dx) / dist;
  const curvature = Math.min(8, Math.max(0.6, dist * 0.18));
  const control = [mid[0] + (ny * curvature), mid[1] + (nx * curvature)];

  return quadraticBezierPoints(a, control, c, steps);
}

function toVec(lat, lon) {
  const φ = (lat * Math.PI) / 180;
  const λ = (lon * Math.PI) / 180;
  return [Math.cos(φ) * Math.cos(λ), Math.cos(φ) * Math.sin(λ), Math.sin(φ)];
}

function fromVec([x, y, z]) {
  return [
    (Math.atan2(z, Math.sqrt(x * x + y * y)) * 180) / Math.PI,
    (Math.atan2(y, x) * 180) / Math.PI,
  ];
}

function slerpVec(a, b, t) {
  const dot = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
  const angle = Math.acos(dot);
  if (Math.abs(angle) < 1e-10) return [...a];
  const s = Math.sin(angle);
  return a.map((v, i) => (Math.sin((1 - t) * angle) / s) * v + (Math.sin(t * angle) / s) * b[i]);
}

export function buildGreatCirclePolyline(from, to, steps = 80) {
  const a = [Number(from?.[0]), Number(from?.[1])];
  const c = [Number(to?.[0]), Number(to?.[1])];
  if (!Number.isFinite(a[0]) || !Number.isFinite(a[1]) || !Number.isFinite(c[0]) || !Number.isFinite(c[1])) return [];
  const va = toVec(a[0], a[1]);
  const vc = toVec(c[0], c[1]);
  const pts = [];
  for (let i = 0; i <= steps; i++) {
    pts.push(fromVec(slerpVec(va, vc, i / steps)));
  }
  return pts;
}

export const DEFAULT_ROUTE_PATH_OPTIONS = {
  color: '#c49b63',
  weight: 2,
  opacity: 0.9,
};

export const DEFAULT_CONNECTOR_PATH_OPTIONS = {
  color: '#c49b63',
  weight: 1.5,
  opacity: 0.22,
  dashArray: '5, 8',
};

// Activity-to-activity walking routes — a distinct, clearly-visible style so the
// in-city path you'll actually walk stands out from the faint city connectors and
// the gold flight arcs.
export const DEFAULT_WALK_PATH_OPTIONS = {
  color: '#38bdf8',
  weight: 4,
  opacity: 0.9,
};
